import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp,
  Users,
  MousePointer,
  Target,
  Facebook,
  Instagram,
  MapPin,
  Car,
  Smartphone,
  CreditCard,
  FileText,
  Search,
} from "lucide-react";

interface TrafficSource {
  source: string;
  visitors: number;
  percentage: number;
}

interface SourceDetail {
  source: string;
  visitors: number;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

interface TrafficSourceChartProps {
  utmSources: Record<string, number>;
  searchEngines?: Record<string, number>;
  referrers?: Record<string, number>;
  totalVisitors: number;
}

// Color palette for different sources
const COLORS = [
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#06B6D4",
  "#84CC16",
  "#F97316",
  "#EC4899",
  "#6366F1",
];

// Source configurations
const SOURCE_CONFIG: Record<
  string,
  { label: string; icon: React.ReactNode; color: string; description: string }
> = {
  website: {
    label: "Website Direct",
    icon: <MousePointer className="h-4 w-4" />,
    color: "#3B82F6",
    description: "Direct website visits",
  },
  facebook: {
    label: "Facebook Organic",
    icon: <Facebook className="h-4 w-4" />,
    color: "#1877F2",
    description: "Organic Facebook posts",
  },
  facebook_ads: {
    label: "Facebook Ads",
    icon: <Target className="h-4 w-4" />,
    color: "#1565C0",
    description: "Paid Facebook advertising",
  },
  instagram: {
    label: "Instagram Organic",
    icon: <Instagram className="h-4 w-4" />,
    color: "#E4405F",
    description: "Organic Instagram posts",
  },
  instagram_ads: {
    label: "Instagram Ads",
    icon: <Target className="h-4 w-4" />,
    color: "#C13584",
    description: "Paid Instagram advertising",
  },
  tiktok_ads: {
    label: "TikTok Ads",
    icon: <Target className="h-4 w-4" />,
    color: "#000000",
    description: "Paid TikTok advertising",
  },
  google_organic: {
    label: "Google Organic",
    icon: <Search className="h-4 w-4" />,
    color: "#34A853",
    description: "Google search results",
  },
  google_ads: {
    label: "Google Ads",
    icon: <Target className="h-4 w-4" />,
    color: "#4285F4",
    description: "Google paid advertising",
  },
  google_maps: {
    label: "Google Maps",
    icon: <MapPin className="h-4 w-4" />,
    color: "#34A853",
    description: "Google Maps listings",
  },
  google_maps_ads: {
    label: "Google Maps Ads",
    icon: <Target className="h-4 w-4" />,
    color: "#1976D2",
    description: "Google Maps advertising",
  },
  whatsapp: {
    label: "WhatsApp Organic",
    icon: <Smartphone className="h-4 w-4" />,
    color: "#25D366",
    description: "WhatsApp messages",
  },
  whatsapp_ads: {
    label: "WhatsApp Ads",
    icon: <Target className="h-4 w-4" />,
    color: "#128C7E",
    description: "WhatsApp advertising",
  },
  business_card: {
    label: "Business Card QR",
    icon: <CreditCard className="h-4 w-4" />,
    color: "#6366F1",
    description: "QR code on business cards",
  },
  poster: {
    label: "Shop Poster QR",
    icon: <FileText className="h-4 w-4" />,
    color: "#8B5CF6",
    description: "QR code on shop posters",
  },
  car_door: {
    label: "Car Door Sign QR",
    icon: <Car className="h-4 w-4" />,
    color: "#F59E0B",
    description: "QR code on car door signs",
  },
  window_sticker: {
    label: "Car Window Sticker",
    icon: <Car className="h-4 w-4" />,
    color: "#EF4444",
    description: "QR code on window stickers",
  },
  flyer: {
    label: "Flyers",
    icon: <FileText className="h-4 w-4" />,
    color: "#10B981",
    description: "QR code on flyers",
  },
};

export const TrafficSourceChart: React.FC<TrafficSourceChartProps> = ({
  utmSources,
  searchEngines = {},
  referrers = {},
  totalVisitors,
}) => {
  // Process UTM sources data
  const utmData: SourceDetail[] = Object.entries(utmSources)
    .map(([source, visitors]) => ({
      source,
      visitors,
      label: SOURCE_CONFIG[source]?.label || source.replace("_", " "),
      description:
        SOURCE_CONFIG[source]?.description || `Traffic from ${source}`,
      icon: SOURCE_CONFIG[source]?.icon || <MousePointer className="h-4 w-4" />,
      color: SOURCE_CONFIG[source]?.color || COLORS[0],
    }))
    .sort((a, b) => b.visitors - a.visitors);

  // Calculate percentages
  const chartData = utmData.map((item) => ({
    ...item,
    percentage: (item.visitors / totalVisitors) * 100,
  }));

  // Top sources for pie chart
  const pieData = chartData.slice(0, 8);

  // Performance metrics
  const topSource = chartData[0];
  const totalUTMVisitors = Object.values(utmSources).reduce(
    (sum, count) => sum + count,
    0,
  );

  const formatTooltip = (value: any, name: string) => {
    if (name === "visitors") {
      const item = chartData.find((d) => d.visitors === value);
      const percentage = item ? item.percentage.toFixed(1) : "0";
      return [`${value} visitors (${percentage}%)`, "Visitors"];
    }
    return [value, name];
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium">Total UTM Visitors</span>
            </div>
            <div className="text-2xl font-bold text-blue-600">
              {totalUTMVisitors}
            </div>
            <div className="text-xs text-gray-600">
              {((totalUTMVisitors / totalVisitors) * 100).toFixed(1)}% of all
              traffic
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium">Top Source</span>
            </div>
            <div className="text-lg font-bold text-green-600">
              {topSource?.label || "N/A"}
            </div>
            <div className="text-xs text-gray-600">
              {topSource?.visitors || 0} visitors (
              {topSource?.percentage.toFixed(1) || 0}%)
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4 text-purple-600" />
              <span className="text-sm font-medium">Active Sources</span>
            </div>
            <div className="text-2xl font-bold text-purple-600">
              {chartData.length}
            </div>
            <div className="text-xs text-gray-600">UTM sources tracked</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <MousePointer className="h-4 w-4 text-orange-600" />
              <span className="text-sm font-medium">Conversion Rate</span>
            </div>
            <div className="text-2xl font-bold text-orange-600">
              {totalUTMVisitors > 0
                ? ((totalUTMVisitors / totalVisitors) * 100).toFixed(1)
                : "0"}
              %
            </div>
            <div className="text-xs text-gray-600">UTM attribution</div>
          </CardContent>
        </Card>
      </div>

      {/* Source Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {chartData.map((source, index) => (
          <Card
            key={source.source}
            className="hover:shadow-lg transition-shadow"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div
                    className="p-2 rounded-lg"
                    style={{
                      backgroundColor: `${source.color}20`,
                      color: source.color,
                    }}
                  >
                    {source.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">{source.label}</h3>
                    <p className="text-xs text-gray-600">
                      {source.description}
                    </p>
                  </div>
                </div>
                <Badge variant="secondary" className="text-xs">
                  #{index + 1}
                </Badge>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span
                    className="text-2xl font-bold"
                    style={{ color: source.color }}
                  >
                    {source.visitors}
                  </span>
                  <span className="text-sm text-gray-600">
                    {source.percentage.toFixed(1)}%
                  </span>
                </div>

                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${Math.min(100, (source.percentage / (topSource?.percentage || 1)) * 100)}%`,
                      backgroundColor: source.color,
                    }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Bar Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Traffic Source Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="label"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fontSize: 11 }}
                />
                <YAxis />
                <Tooltip formatter={formatTooltip} />
                <Bar dataKey="visitors" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Pie Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Top Traffic Sources Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ label, percentage }) =>
                    `${label} ${percentage.toFixed(1)}%`
                  }
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="visitors"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={formatTooltip} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Performance Table */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Performance Metrics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-semibold">Rank</th>
                  <th className="text-left p-3 font-semibold">Source</th>
                  <th className="text-right p-3 font-semibold">Visitors</th>
                  <th className="text-right p-3 font-semibold">Percentage</th>
                  <th className="text-right p-3 font-semibold">Performance</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((source, index) => (
                  <tr key={source.source} className="border-b hover:bg-gray-50">
                    <td className="p-3 text-center">
                      <span
                        className="inline-flex items-center justify-center w-6 h-6 rounded-full text-sm font-bold text-white"
                        style={{ backgroundColor: source.color }}
                      >
                        {index + 1}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div style={{ color: source.color }}>{source.icon}</div>
                        <div>
                          <div className="font-medium">{source.label}</div>
                          <div className="text-xs text-gray-600">
                            {source.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-right font-semibold">
                      {source.visitors}
                    </td>
                    <td className="p-3 text-right text-gray-600">
                      {source.percentage.toFixed(1)}%
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div
                            className="h-2 rounded-full"
                            style={{
                              width: `${Math.min(100, (source.percentage / (topSource?.percentage || 1)) * 100)}%`,
                              backgroundColor: source.color,
                            }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
